import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { brl, toISODateInput } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, LineChart, Line } from "recharts";
import { TrendingUp, DollarSign, Users, Scissors, Receipt, Loader2, ExternalLink, Download, Filter, FileText } from "lucide-react";
import { toast } from "sonner";
import { errMsg, getToken, API } from "@/lib/api";
import { formatAppointmentServices } from "@/lib/appointment";

const Stat = ({ label, value, icon: Icon, tid }) => (
  <div className="surface p-6 flex flex-col gap-2" data-testid={tid}>
    <div className="flex items-center justify-between">
      <span className="text-xs uppercase tracking-widest text-muted-foreground">{label}</span>
      <Icon className="h-4 w-4 text-primary" />
    </div>
    <div className="text-2xl font-display font-bold tracking-tight">{value}</div>
  </div>
);

export default function Reports() {
  const today = new Date();
  const monthAgo = new Date(); monthAgo.setDate(monthAgo.getDate() - 30);
  const [start, setStart] = useState(toISODateInput(monthAgo));
  const [end, setEnd] = useState(toISODateInput(today));
  const [data, setData] = useState(null);

  // Billing tab — filter appointments per client to consolidate into a single Asaas charge
  const [clients, setClients] = useState([]);
  const [billClient, setBillClient] = useState("");
  const [billStart, setBillStart] = useState(toISODateInput(monthAgo));
  const [billEnd, setBillEnd] = useState(toISODateInput(today));
  const [billStatus, setBillStatus] = useState("completed");
  const [billingType, setBillingType] = useState("PIX");
  // Se ligado, chama Asaas /invoices logo após criar a cobrança para agendar
  // a NF-e (NFS-e). O backend valida a existência do CEP/endereço no cliente,
  // mas fazemos o gate aqui também para dar mensagem clara e link p/ editar.
  const [issueNfe, setIssueNfe] = useState(false);
  const [billAppts, setBillAppts] = useState([]);
  const [selectedAppts, setSelectedAppts] = useState({}); // id -> boolean
  const [billLoading, setBillLoading] = useState(false);
  const [charging, setCharging] = useState(false);
  const [lastInvoice, setLastInvoice] = useState(null);

  // Detailed commissions tab — per-professional list with PDF export
  const [pros, setPros] = useState([]);
  const [commStart, setCommStart] = useState(toISODateInput(monthAgo));
  const [commEnd, setCommEnd] = useState(toISODateInput(today));
  const [commPro, setCommPro] = useState("__all__");
  const [commData, setCommData] = useState(null);
  const [commLoading, setCommLoading] = useState(false);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/clients"); setClients(data || []); } catch {}
      try { const { data } = await api.get("/professionals"); setPros((data || []).filter((p) => p.active !== false)); } catch {}
    })();
  }, []);

  const loadCommissions = async () => {
    setCommLoading(true);
    try {
      const s = new Date(commStart + "T00:00:00").toISOString();
      const e = new Date(commEnd + "T23:59:59").toISOString();
      const params = { start: s, end: e };
      if (commPro && commPro !== "__all__") params.professional_id = commPro;
      const { data } = await api.get("/reports/commissions", { params });
      setCommData(data);
      // Auto-expand when a single professional is selected
      if (commPro !== "__all__" && data?.professionals?.length) {
        setExpanded({ [data.professionals[0].professional_id]: true });
      }
    } catch (err) { toast.error(errMsg(err, "Falha ao gerar comissões")); }
    finally { setCommLoading(false); }
  };
  useEffect(() => { loadCommissions(); /* eslint-disable-next-line */ }, [commStart, commEnd, commPro]);

  const downloadCommissionsPdf = () => {
    const s = new Date(commStart + "T00:00:00").toISOString();
    const e = new Date(commEnd + "T23:59:59").toISOString();
    const params = new URLSearchParams({ start: s, end: e, auth: getToken() || "" });
    if (commPro && commPro !== "__all__") params.set("professional_id", commPro);
    // Server ignores ?auth for non-uploads routes; use it only to allow browser
    // download. Route requires Authorization header, so we build a fetch with
    // headers and trigger the download from a blob.
    (async () => {
      try {
        const res = await fetch(`${API}/reports/commissions/pdf?${new URLSearchParams({ ...(commPro !== "__all__" ? { professional_id: commPro } : {}), start: s, end: e })}`, {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        if (!res.ok) throw new Error(await res.text());
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `comissoes_${commStart}_${commEnd}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
      } catch (err) { toast.error(errMsg(err, "Falha ao baixar PDF")); }
    })();
  };

  const downloadClientPdf = async () => {
    if (!billClient) { toast.error("Selecione um cliente"); return; }
    const s = new Date(billStart + "T00:00:00").toISOString();
    const e = new Date(billEnd + "T23:59:59").toISOString();
    try {
      const res = await fetch(`${API}/reports/client/pdf?${new URLSearchParams({ client_id: billClient, start: s, end: e, status: billStatus })}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error(await res.text());
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const clientName = (clients.find((c) => c.id === billClient)?.name || "cliente").replace(/\s+/g, "_");
      a.download = `historico_${clientName}_${billStart}_${billEnd}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(errMsg(err, "Falha ao baixar PDF do cliente")); }
  };

  const load = async () => {
    const s = new Date(start + "T00:00:00").toISOString();
    const e = new Date(end + "T23:59:59").toISOString();
    const { data } = await api.get("/reports/summary", { params: { start: s, end: e } });
    setData(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [start, end]);

  const loadBill = async (opts = {}) => {
    if (!billClient) { toast.error("Selecione um cliente"); return; }
    setBillLoading(true);
    try {
      const s = new Date(billStart + "T00:00:00").toISOString();
      const e = new Date(billEnd + "T23:59:59").toISOString();
      const { data } = await api.get("/appointments", { params: { client_id: billClient, start: s, end: e, status: billStatus } });
      setBillAppts(data);
      const sel = {};
      data.forEach((a) => { if (!a.invoice_id) sel[a.id] = true; });
      setSelectedAppts(sel);
      if (!opts.keepInvoice) setLastInvoice(null);
    } catch (err) { toast.error(errMsg(err, "Falha ao gerar relatório")); }
    finally { setBillLoading(false); }
  };

  const selectedList = useMemo(() => billAppts.filter((a) => selectedAppts[a.id]), [billAppts, selectedAppts]);
  const selectedTotal = useMemo(() => selectedList.reduce((s, a) => s + Number(a.price || 0), 0), [selectedList]);

  const chargeSelected = async () => {
    if (!billClient) { toast.error("Selecione um cliente"); return; }
    const ids = selectedList.filter((a) => !a.invoice_id).map((a) => a.id);
    if (ids.length === 0) { toast.error("Selecione ao menos um atendimento não cobrado"); return; }
    // Ao pedir NF-e, o Asaas exige CEP + endereço do cliente. Bloqueamos aqui
    // com uma mensagem clara e um atalho para a página Clientes — assim o
    // usuário não perde a cobrança preparada por causa de um campo vazio.
    const clientDoc = clients.find((c) => c.id === billClient);
    if (issueNfe) {
      const missing = [];
      if (!clientDoc?.cpf_cnpj) missing.push("CPF/CNPJ");
      if (!clientDoc?.cep) missing.push("CEP");
      if (!clientDoc?.address_street) missing.push("Rua");
      if (!clientDoc?.address_number) missing.push("Número");
      if (!clientDoc?.city) missing.push("Cidade");
      if (!clientDoc?.state) missing.push("UF");
      if (missing.length) {
        const go = window.confirm(
          `Para emitir NF-e o Asaas exige o endereço completo do cliente.\n\nFalta: ${missing.join(", ")}.\n\nAbrir Clientes para completar agora?`
        );
        if (go) window.location.href = "/clientes";
        return;
      }
    }
    setCharging(true);
    try {
      const { data } = await api.post("/invoices", {
        client_id: billClient,
        appointment_ids: ids,
        billing_type: billingType,
        issue_invoice: !!issueNfe,
      });
      setLastInvoice(data);
      if (data?.nfe) {
        if (data.nfe.issued) toast.success("NF-e agendada no Asaas");
        else toast.warning(data.nfe.detail || "NF-e não emitida — verifique no Asaas");
      }
      toast.success("Cobrança criada no Asaas");
      loadBill({ keepInvoice: true });
    } catch (err) { toast.error(errMsg(err, "Falha ao gerar cobrança")); }
    finally { setCharging(false); }
  };

  return (
    <div className="space-y-8 animate-fade-in-up">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-display font-bold tracking-tight">Relatórios</h1>
          <p className="text-sm text-muted-foreground mt-1">Faturamento, comissões e produtividade.</p>
        </div>
        <div className="flex gap-3 items-end">
          <div><Label className="text-xs">De</Label><Input data-testid="input-start" type="date" value={start} onChange={(e) => setStart(e.target.value)} className="h-11 w-40" /></div>
          <div><Label className="text-xs">Até</Label><Input data-testid="input-end" type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="h-11 w-40" /></div>
          <Button data-testid="btn-apply-report" onClick={load} className="h-11">Aplicar</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Faturamento" value={brl(data?.total_revenue)} icon={DollarSign} tid="rep-revenue" />
        <Stat label="Líquido" value={brl(data?.net_revenue)} icon={TrendingUp} tid="rep-net" />
        <Stat label="Comissões" value={brl(data?.total_commissions)} icon={Scissors} tid="rep-commissions" />
        <Stat label="Atendimentos" value={data?.completed_appointments || 0} icon={Users} tid="rep-count" />
      </div>
      <div className="text-sm text-muted-foreground" data-testid="rep-tolls">Pedágios (inclusos no faturamento): <span className="font-semibold text-foreground">{brl(data?.total_tolls || 0)}</span></div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="surface p-6">
          <h2 className="font-display font-semibold text-lg mb-4">Faturamento diário</h2>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={data?.daily_revenue || []} margin={{ left: -10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="date" fontSize={11} stroke="hsl(var(--muted-foreground))" />
              <YAxis fontSize={11} stroke="hsl(var(--muted-foreground))" />
              <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} formatter={(v) => brl(v)} />
              <Line type="monotone" dataKey="revenue" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="surface p-6">
          <h2 className="font-display font-semibold text-lg mb-4">Comissões por profissional</h2>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={data?.by_professional || []} margin={{ left: -10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="professional_name" fontSize={11} stroke="hsl(var(--muted-foreground))" />
              <YAxis fontSize={11} stroke="hsl(var(--muted-foreground))" />
              <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} formatter={(v) => brl(v)} />
              <Bar dataKey="commission" fill="hsl(var(--accent))" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="surface p-6">
        <h2 className="font-display font-semibold text-lg mb-4">Comissões a pagar</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-muted-foreground border-b border-border">
              <tr><th className="py-2 pr-4">Profissional</th><th className="py-2 pr-4">Atendimentos</th><th className="py-2 pr-4">Receita gerada</th><th className="py-2">Comissão</th></tr>
            </thead>
            <tbody>
              {(data?.by_professional || []).map((p) => (
                <tr key={p.professional_id} className="border-b border-border last:border-0" data-testid={`row-pro-${p.professional_id}`}>
                  <td className="py-3 pr-4 font-medium">{p.professional_name}</td>
                  <td className="py-3 pr-4">{p.count}</td>
                  <td className="py-3 pr-4">{brl(p.revenue)}</td>
                  <td className="py-3 font-semibold">{brl(p.commission)}</td>
                </tr>
              ))}
              {(!data || data.by_professional.length === 0) && <tr><td colSpan={4} className="py-8 text-center text-muted-foreground">Sem dados no período.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <div className="surface p-6 space-y-4" data-testid="section-commissions">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="font-display font-semibold text-lg flex items-center gap-2"><Scissors className="h-5 w-5 text-primary" /> Comissões detalhadas por profissional</h2>
            <p className="text-sm text-muted-foreground mt-1">Só entram <strong>agendamentos concluídos</strong> no período. Baixe em PDF para acertar contas com o técnico.</p>
          </div>
          <Button onClick={downloadCommissionsPdf} data-testid="btn-download-commissions-pdf" disabled={commLoading || !commData?.total_appointments}>
            <Download className="h-4 w-4 mr-2" /> Baixar PDF
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs">De</Label>
            <Input data-testid="input-comm-start" type="date" value={commStart} onChange={(e) => setCommStart(e.target.value)} className="h-11" />
          </div>
          <div>
            <Label className="text-xs">Até</Label>
            <Input data-testid="input-comm-end" type="date" value={commEnd} onChange={(e) => setCommEnd(e.target.value)} className="h-11" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs">Profissional</Label>
            <Select value={commPro} onValueChange={setCommPro}>
              <SelectTrigger data-testid="select-comm-pro" className="h-11"><SelectValue placeholder="Todos" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Todos os profissionais</SelectItem>
                {pros.map((p) => (<SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {commLoading ? (
          <div className="py-8 text-center text-sm text-muted-foreground"><Loader2 className="h-4 w-4 mx-auto animate-spin mb-2" /> Carregando…</div>
        ) : !commData?.professionals?.length ? (
          <div className="py-8 text-center text-sm text-muted-foreground">Nenhum atendimento concluído nesse período.</div>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2">
              <div className="rounded-lg border border-border p-3">
                <div className="text-xs text-muted-foreground">Atendimentos</div>
                <div className="text-xl font-display font-bold" data-testid="comm-total-count">{commData.total_appointments}</div>
              </div>
              <div className="rounded-lg border border-border p-3">
                <div className="text-xs text-muted-foreground">Faturamento</div>
                <div className="text-xl font-display font-bold" data-testid="comm-total-revenue">{brl(commData.total_revenue)}</div>
                <div className="text-[11px] text-muted-foreground mt-0.5" data-testid="comm-total-tolls">Pedágios inclusos: <strong className="text-foreground">{brl(commData.total_tolls || 0)}</strong></div>
              </div>
              <div className="rounded-lg border border-border p-3">
                <div className="text-xs text-muted-foreground">Comissão total</div>
                <div className="text-xl font-display font-bold text-primary" data-testid="comm-total-commission">{brl(commData.total_commission)}</div>
                {(commData.total_commission_overrides || 0) > 0 && (
                  <div className="text-[11px] text-muted-foreground mt-0.5">
                    Próprios: <strong className="text-foreground">{brl(commData.total_commission_own || 0)}</strong> · Sobre outros: <strong className="text-foreground">{brl(commData.total_commission_overrides || 0)}</strong>
                  </div>
                )}
              </div>
            </div>

            {commData.professionals.map((p) => {
              const isOpen = expanded[p.professional_id] ?? false;
              const ownC = p.subtotal_commission_own ?? p.subtotal_commission ?? 0;
              const overC = p.subtotal_commission_overrides || 0;
              const totalC = p.total_commission ?? (ownC + overC);
              return (
                <div key={p.professional_id} className="rounded-lg border border-border" data-testid={`comm-pro-${p.professional_id}`}>
                  <button
                    type="button"
                    onClick={() => setExpanded((x) => ({ ...x, [p.professional_id]: !isOpen }))}
                    className="w-full flex items-center justify-between p-4 hover:bg-muted/40 transition-colors"
                    data-testid={`comm-pro-toggle-${p.professional_id}`}
                  >
                    <div className="text-left">
                      <div className="font-semibold">{p.professional_name}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {p.count} atendimento(s) próprios · Faturou {brl(p.subtotal_revenue)}
                        {overC > 0 && ` · +${(p.overrides || []).reduce((s, o) => s + (o.count || 0), 0)} atendimento(s) de outros`}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-display text-xl font-bold text-primary" data-testid={`comm-pro-total-${p.professional_id}`}>{brl(totalC)}</div>
                      {overC > 0 && (
                        <div className="text-[11px] text-muted-foreground">
                          Próprios {brl(ownC)} + Sobre outros {brl(overC)}
                        </div>
                      )}
                      <div className="text-xs text-muted-foreground">{isOpen ? "clique para ocultar" : "clique para detalhes"}</div>
                    </div>
                  </button>
                  {isOpen && (
                    <div className="border-t border-border p-4 overflow-x-auto space-y-4">
                      {/* Próprios serviços */}
                      {p.appointments.length > 0 && (
                        <div>
                          <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Próprios serviços</div>
                          <table className="w-full text-sm">
                            <thead className="text-left text-muted-foreground border-b border-border/60">
                              <tr>
                                <th className="py-2 pr-3">Data</th>
                                <th className="py-2 pr-3">Cliente</th>
                                <th className="py-2 pr-3">Serviço</th>
                                <th className="py-2 pr-3 text-right">Pedágio</th>
                                <th className="py-2 pr-3 text-right">Valor</th>
                                <th className="py-2 text-right">Comissão</th>
                              </tr>
                            </thead>
                            <tbody>
                              {p.appointments.map((a) => (
                                <tr key={a.id} className="border-b border-border/40 last:border-0">
                                  <td className="py-2 pr-3">{new Date(a.scheduled_at).toLocaleString("pt-BR")}</td>
                                  <td className="py-2 pr-3">{a.client_name || "—"}</td>
                                  <td className="py-2 pr-3">{formatAppointmentServices(a) || "—"}</td>
                                  <td className="py-2 pr-3 text-right">{brl(a.toll || 0)}</td>
                                  <td className="py-2 pr-3 text-right">{brl(a.price)}</td>
                                  <td className="py-2 text-right font-medium">{brl(a.commission)}</td>
                                </tr>
                              ))}
                              <tr className="font-semibold bg-muted/30">
                                <td className="py-2 pr-3" colSpan={3}>Subtotal (próprios)</td>
                                <td className="py-2 pr-3 text-right">{brl(p.subtotal_toll || 0)}</td>
                                <td className="py-2 pr-3 text-right">{brl(p.subtotal_revenue)}</td>
                                <td className="py-2 text-right text-primary">{brl(ownC)}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      )}

                      {/* Overrides — comissão sobre outros profissionais */}
                      {(p.overrides || []).filter((o) => o.count > 0).map((ov, oi) => (
                        <div key={`${p.professional_id}-ov-${oi}`} data-testid={`comm-override-${p.professional_id}-${oi}`}>
                          <div className="text-xs font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-400 mb-2">
                            Sobre serviços de {ov.source_professional_name} · {ov.mode === "percentage" ? `${ov.value}%` : `${brl(ov.value)} fixo`}
                            {ov.label ? ` — ${ov.label}` : ""}
                          </div>
                          <table className="w-full text-sm">
                            <thead className="text-left text-muted-foreground border-b border-border/60">
                              <tr>
                                <th className="py-2 pr-3">Data</th>
                                <th className="py-2 pr-3">Cliente</th>
                                <th className="py-2 pr-3">Serviço</th>
                                <th className="py-2 pr-3 text-right">Valor serviço</th>
                                <th className="py-2 text-right">Comissão</th>
                              </tr>
                            </thead>
                            <tbody>
                              {ov.appointments.map((a) => (
                                <tr key={a.id} className="border-b border-border/40 last:border-0">
                                  <td className="py-2 pr-3">{new Date(a.scheduled_at).toLocaleString("pt-BR")}</td>
                                  <td className="py-2 pr-3">{a.client_name || "—"}</td>
                                  <td className="py-2 pr-3">{formatAppointmentServices(a) || "—"}</td>
                                  <td className="py-2 pr-3 text-right">{brl(a.service_price)}</td>
                                  <td className="py-2 text-right font-medium">{brl(a.commission)}</td>
                                </tr>
                              ))}
                              <tr className="font-semibold bg-amber-50 dark:bg-amber-950/30">
                                <td className="py-2 pr-3" colSpan={4}>Subtotal ({ov.count} atendimento{ov.count === 1 ? "" : "s"})</td>
                                <td className="py-2 text-right text-amber-700 dark:text-amber-400">{brl(ov.subtotal)}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      ))}

                      {overC > 0 && (
                        <div className="pt-3 border-t border-border/60 flex items-center justify-between">
                          <div className="text-sm font-semibold">TOTAL do profissional</div>
                          <div className="font-display text-lg font-bold text-primary">{brl(totalC)}</div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="surface p-6">
        <h2 className="font-display font-semibold text-lg mb-4">Serviços mais realizados</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-muted-foreground border-b border-border">
              <tr><th className="py-2 pr-4">Serviço</th><th className="py-2 pr-4">Quantidade</th><th className="py-2">Faturamento</th></tr>
            </thead>
            <tbody>
              {(data?.by_service || []).map((s) => (
                <tr key={s.service_id} className="border-b border-border last:border-0" data-testid={`row-svc-${s.service_id}`}>
                  <td className="py-3 pr-4 font-medium">{s.service_name}</td>
                  <td className="py-3 pr-4">{s.count}</td>
                  <td className="py-3 font-semibold">{brl(s.revenue)}</td>
                </tr>
              ))}
              {(!data || data.by_service.length === 0) && <tr><td colSpan={3} className="py-8 text-center text-muted-foreground">Sem dados no período.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* --- Cobrança consolidada por cliente --- */}
      <div className="surface p-6 space-y-5" data-testid="section-bill-client">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="font-display font-semibold text-lg flex items-center gap-2"><Receipt className="h-5 w-5 text-primary" />Cobrar cliente por período</h2>
            <p className="text-sm text-muted-foreground mt-1">Selecione o cliente, o período e o status para gerar uma cobrança consolidada no Asaas com todos os atendimentos anexados.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <div className="md:col-span-2">
            <Label className="text-xs">Cliente</Label>
            <Select value={billClient} onValueChange={setBillClient}>
              <SelectTrigger data-testid="select-bill-client" className="h-11"><SelectValue placeholder="Selecione um cliente" /></SelectTrigger>
              <SelectContent>
                {clients.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">De</Label>
            <Input data-testid="input-bill-start" type="date" value={billStart} onChange={(e) => setBillStart(e.target.value)} className="h-11" />
          </div>
          <div>
            <Label className="text-xs">Até</Label>
            <Input data-testid="input-bill-end" type="date" value={billEnd} onChange={(e) => setBillEnd(e.target.value)} className="h-11" />
          </div>
          <div>
            <Label className="text-xs">Status</Label>
            <Select value={billStatus} onValueChange={setBillStatus}>
              <SelectTrigger data-testid="select-bill-status" className="h-11"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="completed">Atendidos (finalizados)</SelectItem>
                <SelectItem value="scheduled">Agendados</SelectItem>
                <SelectItem value="scheduled,completed">Agendados + Atendidos</SelectItem>
                <SelectItem value="canceled">Cancelados</SelectItem>
                <SelectItem value="no_show">Faltas</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Button data-testid="btn-load-bill" onClick={loadBill} disabled={billLoading} className="h-11">
            {billLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Gerar relatório
          </Button>
          <Button data-testid="btn-download-client-pdf" variant="outline" onClick={downloadClientPdf} disabled={!billClient} className="h-11">
            <FileText className="h-4 w-4 mr-2" /> Baixar PDF
          </Button>
          <div className="flex items-center gap-2 ml-auto">
            <Label className="text-xs">Forma de cobrança</Label>
            <Select value={billingType} onValueChange={setBillingType}>
              <SelectTrigger data-testid="select-billing-type" className="h-11 w-40"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem data-testid="billing-type-option-PIX" value="PIX">PIX</SelectItem>
                <SelectItem data-testid="billing-type-option-BOLETO" value="BOLETO">Boleto</SelectItem>
                <SelectItem data-testid="billing-type-option-CREDIT_CARD" value="CREDIT_CARD">Cartão</SelectItem>
              </SelectContent>
            </Select>
            <Button data-testid="btn-charge-selected" onClick={chargeSelected} disabled={charging || selectedList.length === 0} className="h-11">
              {charging ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Cobrar via Asaas ({brl(selectedTotal)})
            </Button>
          </div>
        </div>

        {/* Toggle NF-e — mostra o alerta de endereço faltando ANTES de cobrar. */}
        <label className="flex items-start gap-2 text-xs text-muted-foreground cursor-pointer select-none border border-border rounded-lg p-3 bg-muted/20" data-testid="toggle-report-nfe">
          <input
            type="checkbox"
            checked={issueNfe}
            onChange={(e) => setIssueNfe(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-border accent-primary"
          />
          <span>
            <strong className="text-foreground">Emitir NF-e junto no Asaas</strong>
            <span className="block text-[11px]">
              O Asaas exige CPF/CNPJ + CEP + endereço completo do cliente. Se algum campo faltar,
              você será redirecionado para completar antes de cobrar. Configure também o Município
              em Configurações → Integrações → Asaas.
            </span>
          </span>
        </label>

        {billAppts.length > 0 && (
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead className="text-left text-muted-foreground bg-muted/40">
                <tr>
                  <th className="py-2 px-3 w-10"></th>
                  <th className="py-2 px-3">Data/Hora</th>
                  <th className="py-2 px-3">Serviço</th>
                  <th className="py-2 px-3">Profissional</th>
                  <th className="py-2 px-3">Status</th>
                  <th className="py-2 px-3 text-right">Valor</th>
                  <th className="py-2 px-3">Cobrança</th>
                </tr>
              </thead>
              <tbody>
                {billAppts.map((a) => {
                  const already = !!a.invoice_id;
                  return (
                    <tr key={a.id} className="border-t border-border" data-testid={`row-bill-${a.id}`}>
                      <td className="py-2 px-3">
                        <input
                          type="checkbox"
                          data-testid={`chk-bill-${a.id}`}
                          disabled={already}
                          checked={!!selectedAppts[a.id]}
                          onChange={(e) => setSelectedAppts({ ...selectedAppts, [a.id]: e.target.checked })}
                        />
                      </td>
                      <td className="py-2 px-3">{new Date(a.scheduled_at).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}</td>
                      <td className="py-2 px-3">{formatAppointmentServices(a)}</td>
                      <td className="py-2 px-3">{a.professional_name}</td>
                      <td className="py-2 px-3 capitalize">{a.status}</td>
                      <td className="py-2 px-3 text-right font-semibold">{brl(a.price)}</td>
                      <td className="py-2 px-3">{already ? <span className="text-xs text-muted-foreground">Já cobrado</span> : <span className="text-xs">—</span>}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t border-border bg-muted/30">
                  <td colSpan={5} className="py-3 px-3 text-right text-muted-foreground">Total selecionado</td>
                  <td className="py-3 px-3 text-right font-display font-bold text-lg">{brl(selectedTotal)}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {billAppts.length === 0 && billClient && !billLoading && (
          <div className="text-sm text-muted-foreground text-center py-6" data-testid="bill-empty">Nenhum atendimento encontrado com esse filtro.</div>
        )}

        {lastInvoice && (
          <div className="rounded-lg border border-primary/40 bg-primary/5 p-4 space-y-2" data-testid="bill-result">
            <div className="font-medium">Cobrança criada com sucesso.</div>
            <div className="text-sm text-muted-foreground">Total: <strong className="text-foreground">{brl(lastInvoice.total)}</strong> · {lastInvoice.count} atendimento(s) · Vencimento {lastInvoice.due_date}</div>
            {lastInvoice.invoice_url && (
              <a href={lastInvoice.invoice_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm underline" data-testid="link-invoice-url">
                Abrir fatura no Asaas <ExternalLink className="h-3 w-3" />
              </a>
            )}
            {lastInvoice.nfe && (
              <div className={`text-xs mt-1 ${lastInvoice.nfe.issued ? "text-emerald-600" : "text-amber-600"}`} data-testid="bill-nfe-status">
                {lastInvoice.nfe.issued
                  ? `NF-e agendada no Asaas (id: ${lastInvoice.nfe.invoice_id || "—"})`
                  : `NF-e: ${lastInvoice.nfe.detail || "não emitida"}`}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
